import { Merge, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * The bar shown while rows are selected.
 *
 * It floats above the page rather than sitting in the scroll flow. Selecting
 * something near the bottom of a long list used to mean scrolling back to the
 * top to reach the button — the one moment the control needs to be in reach is
 * exactly when it had gone.
 */
export default function SelectionBar({
  count,
  detail,
  onClear,
  onAction,
  actionLabel = "Merge",
  actionIcon: ActionIcon = Merge,
  actionDisabled = false,
  minimum = 2,
  // An optional second action, shown to the left of the main one.
  secondaryLabel,
  onSecondary,
  secondaryIcon: SecondaryIcon,
  secondaryMinimum = 1,
  // An optional custom control — a dropdown, a small form — sitting between
  // Clear and the named actions. Its own enabled/disabled state is the
  // caller's to manage; this bar just gives it a place to live.
  middle,
  // Pages without a filter bar pin it directly under the header instead.
  top = "top-[132px]",
}) {
  if (!count) return null;

  const short = count < minimum;

  return (
    // Pinned just under the filter bar rather than floating at the bottom, so
    // it sits where the selection is happening and stays reachable however far
    // the list is scrolled.
    <div className={cn(
        "sticky z-10 mb-4 rounded-xl border border-brand bg-brand-light px-4 py-2.5",
        top
      )}
    >
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-[13.5px] font-medium text-brand">{count} selected</span>
        {detail && (
          <span className="text-[12.5px] text-muted2 truncate">
            · {short && !secondaryLabel ? `Pick at least ${minimum}` : detail}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1.5 shrink-0">
          <Button size="sm" variant="ghost" onClick={onClear} aria-label="Clear selection">
            <X className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Clear</span>
          </Button>
          {middle}
          {secondaryLabel && (
            <Button
              size="sm"
              variant="outline"
              disabled={count < secondaryMinimum}
              onClick={onSecondary}
            >
              {SecondaryIcon && <SecondaryIcon className="w-3.5 h-3.5 mr-1.5" />}
              {secondaryLabel}
            </Button>
          )}
          <Button
            size="sm"
            disabled={short || actionDisabled}
            onClick={onAction}
            className="bg-brand hover:bg-brand-hover"
          >
            <ActionIcon className="w-3.5 h-3.5 mr-1.5" />
            {actionLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
